import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap, unwrapWithMeta } from '../utils/unwrapResponse';
import type { ReferentialTag, TechnicianListItem } from './techniciansApi';

/**
 * The assignment cycle, from the back office's side: what a page of missions is assigned to, who
 * can be proposed for one, and the two writes that send and release.
 *
 * The technician's own side — his requests, accepting, refusing — is the mobile screen, so it
 * lands with L4c and is deliberately absent here.
 */

/**
 * Mirrors `InterventionRequestStatus` in `backend/types/interventionRequests.ts`.
 *
 * `superseded` is what an acceptance now writes on the mission's other live requests (D46): the
 * race is real since several technicians may be solicited at once. It is neither a refusal nor a
 * silence. `cancelled` is what a reassignment or a release writes instead.
 */
export const REQUEST_STATUSES = ['pending', 'accepted', 'declined', 'cancelled', 'superseded'] as const;
export type InterventionRequestStatus = (typeof REQUEST_STATUSES)[number];

/**
 * Mirrors `RefusalReasonCode` in `backend/types/interventionRequests.ts`.
 *
 * A code and not free text because it is the one thing that has to be counted (D25): thirty free
 * sentences never say whether the problem is the distance, the slot or the skill. The labels are
 * i18n, resolved by a `switch` of literal keys on the screens.
 */
export const REFUSAL_REASON_CODES = [
  'already_booked',
  'too_far',
  'out_of_skills',
  'unavailable',
  'other',
] as const;
export type RefusalReasonCode = (typeof REFUSAL_REASON_CODES)[number];

export interface InterventionRequest {
  id: number;
  issueKey: string;
  serviceDeskId: number;
  technician: { id: number; username: string; firstName: string | null; lastName: string | null };
  status: InterventionRequestStatus;
  sentAt: string;
  respondedAt: string | null;
  /** Seconds between the send and the answer, computed by the server. */
  responseDelayS: number | null;
  refusalReason: string | null;
  /**
   * Why he refused, as a code (D25). `null` on an acceptance, and `null` too on the refusals
   * recorded before the column existed — those carry text only, and the screens still read it.
   */
  refusalReasonCode: RefusalReasonCode | null;
  customMessage: string | null;
  reminderCount: number;
  lastReminderAt: string | null;
  /**
   * Seconds since the send, computed by Postgres on every read — one clock, and nothing for the
   * screen to derive or drift on. It is what makes a mission left pending with someone who never
   * answers visible on the row, which is the compensation for having no expiry (D5).
   */
  elapsedSeconds: number;
  /**
   * Whether the send **actually** bypassed the geographic filter — not whether it asked to be
   * allowed to. `null` on the rows created before the column existed. Recorded and never
   * recomputed: a technician's perimeter changes, so the answer is lost otherwise (D22 §3).
   */
  sentOutsidePerimeter: boolean | null;
  /** Who validated the send. `null` only on the rows created before the column existed. */
  sentByUserId: number | null;
}

/**
 * One row of the « À qualifier » tab, on the assignment side. The mission itself comes from the
 * Jira-backed listing; a mission absent from the response carries nothing at all — no skill, no
 * request, and no store postal code.
 */
/** A skill code Jira carries that this country's referential does not hold (D66-e). */
export interface UnresolvedSkill {
  kind: 'specialty' | 'expertise';
  code: string;
}

export interface MissionAssignment {
  issueKey: string;
  specialties: ReferentialTag[];
  /** Edited in the drawer beside the specialties since D66b, and weighed by the ranking. */
  expertises: ReferentialTag[];
  /** Empty on nearly every mission — it is a referential gap, not a normal state. */
  unresolvedSkills: UnresolvedSkill[];
  request: InterventionRequest | null;
  /** From the Jira Assets payload. `null` when the ticket carries none — then, and only then, ask. */
  storePostalCode: string | null;
  /**
   * Live solicitations on the mission: status pending or accepted. Never counts cancelled or
   * superseded. It is what lets the row say « 2 sollicités » instead of naming one of them.
   */
  liveRequestCount: number;
}

/**
 * One scored candidate. It **is** a directory row — same visibility rule, same aggregates, same
 * availability — plus what the ranking adds.
 */
export interface InterventionCandidate extends TechnicianListItem {
  /** Mission skills this technician does not carry. A set coverage, never a string equality. */
  uncoveredSpecialtyIds: number[];
  /** `upcoming` counts as available: an absence declared for next month does not exclude today. */
  available: boolean;
  /**
   * Always `null` today. The distance is measured from the default address, which lives on
   * `user_addresses` — delivered by L2c, which is not done. **Never render a fabricated `X km`**:
   * a default distance would be a wrong ranking that looks right.
   */
  distanceKm: number | null;
  score: number;
  /**
   * Missions already **accepted** on the mission's own day, this one excluded. `null` when it is
   * not known — no date, or the count failed — and never `0` then: unknown is not zero.
   */
  sameDayMissions: number | null;
  /** What this technician already answered on this mission. Feeds the "next to try" pick. */
  lastRequestStatus: InterventionRequestStatus | null;
  lastRequestAt: string | null;
}

/**
 * The candidates, with what they were computed from.
 *
 * `subdivision: null` means no department matched the postal code — a **different sentence** on
 * screen from an empty `candidates` with a subdivision, which means nobody covers that department.
 * Only one of the two is the ops team's own to fix.
 */
export interface InterventionCandidatePage {
  subdivision: { id: number; code: string; name: string; countryCode: string } | null;
  missionSpecialties: ReferentialTag[];
  candidates: InterventionCandidate[];
  /**
   * How many technicians of the department the mission's day removed from the list, `0` when the
   * mission carries no date. Without it the screen cannot tell "nobody covers this department"
   * from "they are all away that day" — two sentences, one of them the ops team's to fix.
   */
  unavailableCount: number;
}

/** What sending needs. `replacesRequestId` is what turns the send into a reassignment. */
export interface SendRequestInput {
  issueKey: string;
  serviceDeskId: number;
  technicianUserId: number;
  /** The store's postal code, the same one the candidates were read with. */
  postalCode: string;
  replacesRequestId?: number | null;
  /**
   * Lift the perimeter refusal for this one send. **Only the manual name search sets it**: a pick
   * made by name *is* the deliberate override, the ranked list is not — leaving the L4b guard in
   * full force on every candidate the engine proposed.
   */
  allowOutsidePerimeter?: boolean;
  /**
   * The day to write on the ticket, sent **only when Jira carries none** (D16): the server prefers
   * the date already on the issue, so a value sent over one would simply be ignored.
   */
  interventionDate?: string;
  /** Same rule as the date: sent only when the ticket carries no time. */
  interventionTime?: string;
}

/**
 * What the Jira leg of a write did — reported next to a write that **succeeded**, since the row is
 * committed before Jira is touched and a failure cannot travel as a rejected promise. `null` (the
 * whole `meta` absent) means the desk carries no qualification workflow, and the screen must then
 * claim nothing about Jira. `code` becomes a sentence through `getErrorMessage`.
 */
/**
 * `message` is the backend's log line and **no screen may render it** — it is English, and
 * `utils/errorMessage.ts` documents the drawer that once showed one. `said` is Jira's own REST
 * body, written in the desk's language for somebody who has to act, or `null` when Jira said
 * nothing at all. That `null` is what makes the difference safe to rely on.
 */
export type JiraWriteMeta =
  | { ok: true; dateWritten?: boolean; transitioned?: boolean }
  | {
      ok: false;
      stage: 'date' | 'transition' | 'no-transition' | 'participants' | 'skills';
      code: string;
      message: string;
      said?: string | null;
      /**
       * Which family the skills leg lost (D66b-f′) — `'skills'` means both at once, never a
       * stand-in for one. Absent on every other stage, and the banner then names none.
       */
      field?: 'specialties' | 'expertises' | 'skills';
    };

/** A write, plus what Jira did about it. */
export interface WriteWithJira<T> {
  data: T;
  jira: JiraWriteMeta | null;
}

/**
 * `meta.jira`, narrowed. Anything that is not an object carrying a boolean `ok` reads as "nothing
 * to say about Jira" rather than throwing: an unexpected envelope must not take the screen down.
 */
const jiraOf = (meta: unknown): JiraWriteMeta | null => {
  const jira = (meta as { jira?: unknown } | null)?.jira;
  if (!jira || typeof jira !== 'object') return null;
  return typeof (jira as { ok?: unknown }).ok === 'boolean' ? (jira as JiraWriteMeta) : null;
};

const jsonHeaders = { 'Content-Type': 'application/json' };

const mission = (issueKey: string): string => `admin/issues/${encodeURIComponent(issueKey)}`;

/**
 * What a page of missions is assigned to. One call for the whole page: the alternative is one per
 * row, and the tab shows up to a hundred.
 *
 * An empty page is not asked at all — the endpoint requires at least one key, because a call with
 * none is a round trip whose answer is already known.
 */
export const fetchMissionAssignments = async (
  serviceDeskId: number,
  issueKeys: string[]
): Promise<MissionAssignment[]> => {
  if (issueKeys.length === 0) return [];
  // One comma-separated parameter and never a repeated one: `qs` (Express's query parser) turns a
  // repeated param into an object past its 20-item `arrayLimit`, and this endpoint is asked for a
  // whole Jira page of 100 keys. The server splits, validates and deduplicates.
  const params = new URLSearchParams({
    serviceDeskId: String(serviceDeskId),
    issueKey: issueKeys.join(','),
  });
  return unwrap<MissionAssignment[]>(
    await fetchWithAuth(`admin/issues/intervention-overview?${params}`),
    'Failed to load the mission assignments'
  );
};

/**
 * `interventionDate` is the mission's own day, `YYYY-MM-DD` — availability is answered for it and
 * not for today (D60). Omitted when the ticket carries none, or carries something that is not a
 * calendar day: the parameter is strictly validated server-side, and a whole empty list is a worse
 * answer than an unfiltered one.
 */
export const fetchInterventionCandidates = async (
  issueKey: string,
  serviceDeskId: number,
  postalCode: string,
  interventionDate: string | null
): Promise<InterventionCandidatePage> => {
  const params = new URLSearchParams({ serviceDeskId: String(serviceDeskId), postalCode });
  if (interventionDate) params.set('interventionDate', interventionDate);
  return unwrap<InterventionCandidatePage>(
    await fetchWithAuth(`${mission(issueKey)}/intervention-candidates?${params}`),
    'Failed to load the proposed technicians'
  );
};

/** What a mission requires, both families — the shape the save sends and the one it gets back. */
export interface MissionSkills {
  specialties: ReferentialTag[];
  expertises: ReferentialTag[];
}

/**
 * A mission's required skills, **both families in one write** (D66b-f): saving half of them and
 * believing both went is the failure that decision exists to prevent. An empty list is legal and
 * clears them — an ops mistake must be undoable, and a mission with no skill simply scores every
 * candidate on availability alone.
 */
/**
 * What the mission requires **right now**, read for the drawer that edits it — never taken off the
 * listing payload, which is minutes old and blind to what the webhook wrote since (#R-121).
 */
export const fetchMissionSkills = async (
  issueKey: string,
  serviceDeskId: number
): Promise<MissionSkills> =>
  unwrap<MissionSkills>(
    await fetchWithAuth(`${mission(issueKey)}/skills?serviceDeskId=${serviceDeskId}`),
    'Failed to load the mission skills'
  );

export const saveMissionSkills = async (
  issueKey: string,
  serviceDeskId: number,
  skills: { specialtyIds: number[]; expertiseIds: number[] }
): Promise<WriteWithJira<MissionSkills>> => {
  // The save also PATCHes the two Jira fields, which are the source (D66-a). A refused PATCH
  // leaves the local write standing and travels in `meta.jira`, naming the family that diverged.
  const { data, meta } = await unwrapWithMeta<MissionSkills>(
    await fetchWithAuth(`${mission(issueKey)}/skills?serviceDeskId=${serviceDeskId}`, {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify(skills),
    }),
    'Failed to save the mission skills'
  );
  return { data, jira: jiraOf(meta) };
};

/**
 * Send, or reassign — the same call. With `replacesRequestId` the named request is cancelled and
 * the new one created in the same transaction, which is what lets **Réattribuer** work from
 * `pending` as well as from `declined`.
 *
 * Naming the row rather than saying "the live one" is what makes a stale screen a 409 instead of
 * a silent cancellation.
 */
export const sendInterventionRequest = async ({
  issueKey,
  serviceDeskId,
  technicianUserId,
  postalCode,
  replacesRequestId = null,
  allowOutsidePerimeter = false,
  interventionDate,
  interventionTime,
}: SendRequestInput): Promise<WriteWithJira<InterventionRequest>> => {
  const { data, meta } = await unwrapWithMeta<InterventionRequest>(
    await fetchWithAuth(`${mission(issueKey)}/intervention-requests?serviceDeskId=${serviceDeskId}`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        technicianUserId,
        postalCode,
        replacesRequestId,
        allowOutsidePerimeter,
        ...(interventionDate ? { interventionDate } : {}),
        ...(interventionTime ? { interventionTime } : {}),
      }),
    }),
    'Failed to send the intervention request'
  );
  return { data, jira: jiraOf(meta) };
};

export interface SavedInterventionDate {
  issueKey: string;
  interventionDate: string;
  interventionTime: string | null;
  previous: { interventionDate: string | null; interventionTime: string | null };
  changed: boolean;
  /** Live requests (pending or accepted) whose technician was queued an e-mail. */
  notified: number;
}

/**
 * The mission's intervention date, and its time when given. Accepted while being qualified or
 * scheduled (D73), refused beyond — `INTERVENTION_DATE_LOCKED`, not a silent ignore.
 */
export const saveInterventionDate = async (
  issueKey: string,
  serviceDeskId: number,
  interventionDate: string,
  interventionTime?: string
): Promise<SavedInterventionDate> =>
  unwrap<SavedInterventionDate>(
    await fetchWithAuth(`${mission(issueKey)}/intervention-date?serviceDeskId=${serviceDeskId}`, {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify({ interventionDate, ...(interventionTime ? { interventionTime } : {}) }),
    }),
    'Failed to save the intervention date'
  );

/**
 * Release an assignment: the live request becomes `cancelled` and the mission goes back to having
 * no technician. Not a `DELETE` — the row stays, and what it records is what the follow-up reads.
 *
 * On a desk that qualifies, this also puts the ticket back on « en cours de qualification » so the
 * mission returns to the tab (PO, 09/09) — `jira` is what says whether that half worked.
 */
export const cancelInterventionRequest = async (
  issueKey: string,
  serviceDeskId: number,
  requestId: number
): Promise<WriteWithJira<MissionAssignmentDetail>> => {
  // The route answers `missionAssignment()` — the mission's requests **and** its Jira participants
  // — not the bare request list it used to. Declaring the old shape made TypeScript vouch for an
  // array that never arrives.
  const { data, meta } = await unwrapWithMeta<MissionAssignmentDetail>(
    await fetchWithAuth(
      `${mission(issueKey)}/intervention-requests/${requestId}/cancel?serviceDeskId=${serviceDeskId}`,
      { method: 'POST' }
    ),
    'Failed to release the intervention request'
  );
  return { data, jira: jiraOf(meta) };
};

/* ------------------------------------------------------------------ the follow-up */

/**
 * One row of the follow-up table. Mirrors `FollowUpRequestView` — the request, plus the little of
 * the mission the ops team needs to recognise it.
 *
 * Every mission field is nullable because the join is `LEFT`: `sync-children-tables.js` TRUNCATEs
 * `issues`, and a request whose mission has vanished stays in the table rendered `—` rather than
 * silently leaving the follow-up.
 */
export interface FollowUpRequest extends InterventionRequest {
  store: string | null;
  /** The town, from the Jira Assets payload — what the design puts beside the store name. */
  storeCity: string | null;
  requestType: string | null;
  /** The mission's own text. Read by the drawer, by nothing else. */
  description: string | null;
  /** The desk's own Jira field (D15), day only — never a time, because none is stored. */
  interventionDate: string | null;
  /** Who validated the send. `null` on rows older than the column, or on a deleted sender. */
  sentByUsername: string | null;
  /**
   * What the **job** needs, and what the **technician** can do. Two different questions, so two
   * lists: the design puts the first on the mission row and the second under the name.
   */
  missionSpecialties: string[];
  technicianSpecialties: string[];
  /**
   * The technician's country and departments, for the drawer's identity card. Pre-formatted
   * (`75 — Paris, 92 — Hauts-de-Seine`) because it is printed, never sorted or filtered on.
   * `null` on an account whose record has not been filled — rendered `—`, never guessed.
   */
  /** Stated by the backend's join, never inferred from the display fields being null. */
  missionKnown: boolean;
  /**
   * How many requests this mission carries in the **filtered** population (D47). The row shows
   * « + N sollicités » with N = this minus the one displayed, and nothing at all when it is 1.
   */
  missionRequestCount: number;
  /**
   * Of those, how many can still answer — `pending` or `accepted`. What decides whether the row
   * may name a technician at all (D54); `missionRequestCount` counts refusals and cancellations too.
   */
  liveRequestCount: number;
}

/**
 * The counter cards: per status, the requests **and** the missions they sit on (D48). Two figures
 * and not one, because several statuses now coexist on a mission — « 12 en attente » is not 12
 * missions. Counted over the current filters minus the status one, so narrowing to one card does
 * not leave the others reading zero.
 */
export type FollowUpStats = Record<
  InterventionRequestStatus,
  { requests: number; missions: number }
>;

/**
 * Mirrors the backend's `FOLLOW_UP_ORDER_BY`. **Status and refusal reason are not in it** (D37):
 * they are translated labels, and an alphabetical order over them answers no question — they are
 * filters instead. No `elapsed` key either: waiting time is `NOW() - sentAt`, so the oldest send
 * *is* the longest wait, and one column carries both.
 */
export const FOLLOW_UP_ORDER_BY = [
  'sentAt',
  'interventionDate',
  'technician',
  'store',
  'responseDelay',
] as const;
export type FollowUpOrderBy = (typeof FOLLOW_UP_ORDER_BY)[number];

export interface FollowUpFilters {
  serviceDeskId: number;
  status?: InterventionRequestStatus[];
  technicianId?: number[];
  refusalReasonCode?: RefusalReasonCode[];
  /** Only ever set to `true`: "show me the bypasses" (D22 §3), never "the compliant ones". */
  outsidePerimeter?: boolean;
  search?: string;
  sentFrom?: string;
  sentTo?: string;
  interventionFrom?: string;
  interventionTo?: string;
  orderBy: FollowUpOrderBy;
  order: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export interface FollowUpPage {
  /** One row per **mission** since D47 — the retained request, plus its mission's request count. */
  requests: FollowUpRequest[];
  /** Missions, which is what pages the table. */
  total: number;
  /** The requests behind those missions — the footer's second figure, never the same number. */
  totalRequests: number;
  stats: FollowUpStats;
  page: number;
  pageSize: number;
}

/**
 * The view, as a query string. Exported because the screen keeps its state in the URL: one
 * serializer, so the address bar and the request cannot drift apart.
 */
export const followUpQuery = (filters: FollowUpFilters): string => {
  const params = new URLSearchParams({
    serviceDeskId: String(filters.serviceDeskId),
    orderBy: filters.orderBy,
    order: filters.order,
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });
  if (filters.search) params.set('search', filters.search);
  // Only when true. `?outsidePerimeter=false` is meaningless — the column is nullable and a
  // `NULL` predates it rather than recording a compliant send.
  if (filters.outsidePerimeter) params.set('outsidePerimeter', 'true');

  const dates: Array<[string, string | undefined]> = [
    ['sentFrom', filters.sentFrom],
    ['sentTo', filters.sentTo],
    ['interventionFrom', filters.interventionFrom],
    ['interventionTo', filters.interventionTo],
  ];
  for (const [key, value] of dates) if (value) params.set(key, value);

  const lists: Array<[string, Array<string | number> | undefined]> = [
    ['status', filters.status],
    ['technicianId', filters.technicianId],
    ['refusalReasonCode', filters.refusalReasonCode],
  ];
  for (const [key, values] of lists) if (values?.length) params.set(key, values.join(','));

  return params.toString();
};

/**
 * Whether the view is narrowed. Beside the serializer for the same reason: the screen picks its
 * empty message from it and the bar shows « Réinitialiser » from it, and the two must agree.
 *
 * The desk is **not** a filter — it is the picker every screen of this family carries (D40) — and
 * the sort is not one either: neither narrows what is shown.
 */
export const hasActiveFollowUpFilters = (filters: FollowUpFilters): boolean =>
  !!(
    filters.search ||
    filters.status?.length ||
    filters.technicianId?.length ||
    filters.refusalReasonCode?.length ||
    filters.outsidePerimeter ||
    filters.sentFrom ||
    filters.sentTo ||
    filters.interventionFrom ||
    filters.interventionTo
  );

export const fetchFollowUpRequests = async (filters: FollowUpFilters): Promise<FollowUpPage> =>
  unwrap<FollowUpPage>(
    await fetchWithAuth(`admin/intervention-requests?${followUpQuery(filters)}`),
    'Failed to fetch the intervention requests'
  );

/**
 * One participant of the mission's Jira access list, reconciled with our own accounts (D50).
 *
 * GPS **never writes this list beyond adding the technician who accepted** and offers no way to
 * remove anyone: it is read to be said out loud, not to be acted upon.
 */
export interface MissionParticipant {
  accountId: string;
  displayName: string;
  /** Our technician account behind this Jira user, or `null` — an ECM, a DRV, a never-seen one. */
  technicianUserId: number | null;
  /** Solicited through GPS on this mission, whatever the request answered. */
  solicited: boolean;
  addedByName: string | null;
  addedAt: string | null;
}

/** What one mission's assignment is made of: the GPS requests, and the Jira list beside them. */
export interface MissionAssignmentDetail {
  requests: InterventionRequest[];
  participants: MissionParticipant[];
}

/**
 * Every request sent for one mission, newest first — and the Jira access list beside them.
 *
 * The whole history, refusals included: a follow-up that showed only the live request would hide
 * exactly what an ops is looking for when a mission has been going round in circles.
 */
export const fetchMissionRequests = async (
  issueKey: string,
  serviceDeskId: number
): Promise<MissionAssignmentDetail> =>
  unwrap<MissionAssignmentDetail>(
    await fetchWithAuth(`${mission(issueKey)}/intervention-requests?serviceDeskId=${serviceDeskId}`),
    'Failed to fetch the mission requests'
  );
